import { useEffect, useMemo } from 'react'

import { usePingAuth } from '@entities/User'

import { useAddressStore } from '@features/shipping-methods'

import { useCartInfoManager } from '../hooks/useCartInfoManager'
import { useCartStore } from '../model/useCartStore'


/**
 * Изолированный компонент для управления состоянием корзины.
 * Ререндерится при изменениях корзины, но не влияет на родительские компоненты.
 * Рендерит null — только выполняет side-effects.
 */
export function CartManager () {
  const { data: authData } = usePingAuth()
  const shopId = useAddressStore((state) => state.shopId)

  // Функции из стора (стабильные ссылки)
  const setShop = useCartStore((state) => state.setShop)
  const getCart = useCartStore((state) => state.getCart)
  const removeZeroAmountItemsInCurrentCart = useCartStore((state) => state.removeZeroAmountItemsInCurrentCart)
  const setCurrentCart = useCartStore((state) => state.setCurrentCart)
  const loadCarts = useCartStore((state) => state.loadCarts)
  const syncCart = useCartStore((state) => state.syncCart)
  const checkProductsInStock = useCartStore((state) => state.checkProductsInStock)

  // Данные корзины через селекторы
  const currentShopId = useCartStore((state) => state.currentShopId)
  const cartData = useCartStore((state) => currentShopId ? state.carts[currentShopId] : undefined)

  // Стабильный объект cart
  const cart = useMemo(() => {
    if (!cartData || !currentShopId) return undefined
    return {
      shop_id: currentShopId,
      items: cartData.items,
      promocode: cartData.promocode,
      appliedBonuses: cartData.appliedBonuses ?? [],
    }
  }, [ cartData, currentShopId ])

  const saleConfig = useMemo(() => ({
    spendBonuses: cart?.appliedBonuses ?? [],
    coupons: cart?.promocode ? [ { barcode: cart.promocode } ] : [],
    applySales: [],
    /**
     * NOTE
     * Because we determine promocode/coupon on backend - we dont have to pass it here
     * In case we got numeric promocode - it will used as coupon, otherwise - promocode
     */
    promocodes: []
  }), [ cart?.appliedBonuses, cart?.promocode ])

  // Мемоизируем items (включаем все товары, даже раскупленные, для получения информации)
  const cartItems = useMemo(() =>
    cart?.items
      .filter(item => item.amount > 0)
      .map(({ product_id, amount }) => ({
        product_id,
        amount,
      })) ?? [],
  [ cart?.items ])

  // Мемоизируем переменные для useCartInfoManager
  const cartInfoVariables = useMemo(() =>
    shopId
      ? {
        cartInfo: {
          shop_id: shopId,
          items: cartItems,
          sale: saleConfig,
        },
      }
      : null,
  [ shopId, cartItems, saleConfig ])

  // Single cartInfo fetcher with debounce, stores data in Zustand
  useCartInfoManager(cartInfoVariables)

  // Периодическая проверка доступности товаров (каждую минуту)
  useEffect(() => {
    const interval = setInterval(() => {
      checkProductsInStock()
    }, 60000)

    return () => clearInterval(interval)
  }, [ checkProductsInStock ])

  // Синхронизация корзины при смене магазина или авторизации
  useEffect(() => {
    const currentCart = getCart()
    setShop(shopId)

    const handleSync = async () => {
      const isAuthorized = authData && !authData.requireRegistration

      if (!isAuthorized) return

      await loadCarts()

      setCurrentCart({
        items: currentCart?.items || [],
        promocode: currentCart?.promocode,
        appliedBonuses: currentCart?.appliedBonuses ?? [],
        appliedSales: currentCart?.appliedSales ?? [],
      })
      await checkProductsInStock()
      await removeZeroAmountItemsInCurrentCart(isAuthorized)
      await syncCart()
    }

    handleSync()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ shopId, authData ])

  return null
}

export default CartManager
